import { useState, useEffect, useRef } from 'react';
import api, { filesAPI, customersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  FolderOpen, 
  Upload,
  Download,
  Trash2,
  FileText,
  FileImage,
  File,
  Search,
  Archive,
  Link2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function AdminFiles() {
  const [files, setFiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const customerSearchRef = useRef(null);
  
  // Archive attachment state
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveDocuments, setArchiveDocuments] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedArchiveDoc, setSelectedArchiveDoc] = useState(null);
  const [archiveCustomer, setArchiveCustomer] = useState('');
  const [archiveCustomerSearch, setArchiveCustomerSearch] = useState('');
  const [showArchiveCustomerDropdown, setShowArchiveCustomerDropdown] = useState(false);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [filesRes, customersRes] = await Promise.all([
        filesAPI.getAll(),
        customersAPI.getAll(),
      ]);
      setFiles(filesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Dateien');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast.error('Datei zu groß (max. 25 MB)');
        return;
      }
      // Check file type
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error('Dateityp nicht erlaubt');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedCustomer || !selectedFile) return;
    
    setUploading(true);
    try {
      await filesAPI.upload(selectedCustomer, selectedFile);
      toast.success('Datei erfolgreich hochgeladen');
      setShowUploadDialog(false);
      setSelectedCustomer('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await filesAPI.download(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    if (!deleteFileId) return;
    
    try {
      await filesAPI.delete(deleteFileId);
      toast.success('Datei gelöscht');
      setDeleteFileId(null);
      fetchData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const getFileIcon = (type) => {
    if (type === '.pdf') return FileText;
    if (['.jpg', '.jpeg', '.png'].includes(type)) return FileImage;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.first_name} ${customer.last_name}` : 'Unbekannt';
  };

  const getCustomerNumber = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.customer_number || '';
  };

  // Gefilterte Kunden für die Suche im Upload-Dialog
  const filteredCustomers = customers.filter((customer) => {
    if (!customerSearch) return true;
    const search = customerSearch.toLowerCase();
    const fullName = `${customer.first_name} ${customer.last_name}`.toLowerCase();
    const customerNumber = (customer.customer_number || '').toLowerCase();
    const email = (customer.email || '').toLowerCase();
    return (
      fullName.includes(search) ||
      customerNumber.includes(search) ||
      email.includes(search)
    );
  });

  // Ausgewählten Kunden anzeigen
  const getSelectedCustomerDisplay = () => {
    if (!selectedCustomer) return '';
    const customer = customers.find(c => c.id === selectedCustomer);
    if (!customer) return '';
    return `${customer.customer_number} - ${customer.first_name} ${customer.last_name}`;
  };

  // Kunde auswählen
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer.id);
    setCustomerSearch(`${customer.customer_number} - ${customer.first_name} ${customer.last_name}`);
    setShowCustomerDropdown(false);
  };

  // Dialog zurücksetzen beim Öffnen
  const handleOpenUploadDialog = () => {
    setSelectedCustomer('');
    setSelectedFile(null);
    setCustomerSearch('');
    setShowUploadDialog(true);
  };

  // Archive functions
  const handleOpenArchiveDialog = async () => {
    setShowArchiveDialog(true);
    setArchiveCustomer('');
    setArchiveCustomerSearch('');
    setSelectedArchiveDoc(null);
    setArchiveSearch('');
    await loadArchiveDocuments();
  };

  const loadArchiveDocuments = async (search = '') => {
    setArchiveLoading(true);
    try {
      const response = await api.get(`/admin/documents/list-for-attachment?search=${encodeURIComponent(search)}`);
      setArchiveDocuments(response.data);
    } catch (error) {
      console.error('Failed to load archive:', error);
      toast.error('Archiv konnte nicht geladen werden');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleArchiveSearchChange = (value) => {
    setArchiveSearch(value);
    // Debounce search
    clearTimeout(window.archiveSearchTimeout);
    window.archiveSearchTimeout = setTimeout(() => {
      loadArchiveDocuments(value);
    }, 300);
  };

  const handleSelectArchiveCustomer = (customer) => {
    setArchiveCustomer(customer.id);
    setArchiveCustomerSearch(`${customer.customer_number} - ${customer.first_name} ${customer.last_name}`);
    setShowArchiveCustomerDropdown(false);
  };

  const handleAttachDocument = async () => {
    if (!selectedArchiveDoc || !archiveCustomer) return;
    
    setAttaching(true);
    try {
      await api.post(`/admin/documents/${selectedArchiveDoc.id}/attach-to-customer`, {
        customer_id: archiveCustomer
      });
      toast.success('Dokument an Kunde angehängt');
      setShowArchiveDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to attach document:', error);
      toast.error(error.response?.data?.detail || 'Anhängen fehlgeschlagen');
    } finally {
      setAttaching(false);
    }
  };

  const getDocTypeBadge = (docType) => {
    const types = {
      rechnung: { label: 'RE', color: 'bg-green-100 text-green-700' },
      angebot: { label: 'AN', color: 'bg-blue-100 text-blue-700' },
      auftragsbestaetigung: { label: 'AB', color: 'bg-orange-100 text-orange-700' }
    };
    return types[docType] || { label: docType, color: 'bg-gray-100 text-gray-700' };
  };

  // Filtered customers for archive dialog
  const filteredArchiveCustomers = customers.filter((customer) => {
    if (!archiveCustomerSearch) return true;
    const search = archiveCustomerSearch.toLowerCase();
    const fullName = `${customer.first_name} ${customer.last_name}`.toLowerCase();
    const customerNumber = (customer.customer_number || '').toLowerCase();
    return fullName.includes(search) || customerNumber.includes(search);
  });

  const filteredFiles = files.filter((file) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    const customerName = getCustomerName(file.customer_id).toLowerCase();
    const customerNumber = getCustomerNumber(file.customer_id).toLowerCase();
    return (
      file.original_name.toLowerCase().includes(search) ||
      customerName.includes(search) ||
      customerNumber.includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-files">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-[#1A1A1A] mb-2">
            Dateiverwaltung
          </h1>
          <p className="font-manrope text-gray-600">
            {files.length} Dateien insgesamt
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleOpenArchiveDialog}
            data-testid="archive-btn"
          >
            <Archive className="w-4 h-4 mr-2" />
            Aus Archiv auswählen
          </Button>
          <Button 
            className="btn-primary"
            onClick={handleOpenUploadDialog}
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Datei hochladen
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Suche nach Dateiname oder Kunde..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-files"
            />
          </div>
        </CardContent>
      </Card>

      {/* Files Table */}
      <Card data-testid="files-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Dateiname</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Kunde</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Größe</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Hochgeladen</th>
                  <th className="text-right py-4 px-6 font-barlow font-bold text-sm text-gray-500">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => {
                  const Icon = getFileIcon(file.file_type);
                  return (
                    <tr 
                      key={file.id} 
                      className="border-b last:border-0 table-row-hover"
                      data-testid={`file-row-${file.id}`}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#2E2A88]/10 rounded-lg flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#2E2A88]" />
                          </div>
                          <span className="font-manrope truncate max-w-[200px]">{file.original_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-manrope">{getCustomerName(file.customer_id)}</p>
                          <p className="font-barlow text-sm text-[#9B1B30]">{getCustomerNumber(file.customer_id)}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-manrope text-sm text-gray-600">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="py-4 px-6 font-manrope text-sm text-gray-600">
                        {format(new Date(file.uploaded_at), 'dd.MM.yyyy HH:mm')}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          {file.file_exists === false ? (
                            <span className="text-xs text-red-600 font-medium">Datei fehlt auf Server</span>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownload(file)}
                              data-testid={`download-file-${file.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteFileId(file.id)}
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredFiles.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-manrope text-gray-500">Keine Dateien gefunden</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Datei hochladen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Label className="font-barlow font-bold text-sm">Kunde suchen *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={customerSearchRef}
                  placeholder="Name, Kundennummer oder E-Mail eingeben..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                    if (!e.target.value) setSelectedCustomer('');
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  className="pl-9"
                  data-testid="customer-search"
                />
              </div>
              
              {/* Kunden-Dropdown */}
              {showCustomerDropdown && customerSearch && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.slice(0, 10).map((customer) => (
                      <div
                        key={customer.id}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-0"
                        onClick={() => handleSelectCustomer(customer)}
                        data-testid={`customer-option-${customer.id}`}
                      >
                        <div className="font-medium">
                          {customer.first_name} {customer.last_name}
                        </div>
                        <div className="text-sm text-gray-500 flex gap-2">
                          <span className="text-[#9B1B30] font-medium">{customer.customer_number}</span>
                          <span>•</span>
                          <span>{customer.email}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      Kein Kunde gefunden
                    </div>
                  )}
                </div>
              )}
              
              {/* Ausgewählter Kunde Anzeige */}
              {selectedCustomer && !showCustomerDropdown && (
                <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between">
                  <span className="text-green-800 text-sm font-medium">
                    ✓ {getSelectedCustomerDisplay()}
                  </span>
                  <button 
                    type="button"
                    className="text-green-600 hover:text-green-800 text-sm"
                    onClick={() => {
                      setSelectedCustomer('');
                      setCustomerSearch('');
                    }}
                  >
                    Ändern
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Datei auswählen *</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileSelect}
                className="mt-1"
                data-testid="file-input"
              />
              <p className="font-manrope text-xs text-gray-500 mt-1">
                Erlaubt: PDF, JPG, PNG, DOC, DOCX (max. 25 MB)
              </p>
            </div>
            {selectedFile && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-manrope text-sm">
                  <strong>Ausgewählt:</strong> {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleUpload}
              disabled={!selectedCustomer || !selectedFile || uploading}
              data-testid="upload-submit"
            >
              {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteFileId} onOpenChange={() => setDeleteFileId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow">Datei löschen?</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              Diese Aktion kann nicht rückgängig gemacht werden. Die Datei wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Selection Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Dokument aus Archiv auswählen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Document Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Dokumentnummer oder Kundenname suchen..."
                value={archiveSearch}
                onChange={(e) => handleArchiveSearchChange(e.target.value)}
                className="pl-9"
                data-testid="archive-search"
              />
            </div>
            
            {/* Documents List */}
            <div className="border rounded-lg max-h-[250px] overflow-y-auto">
              {archiveLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1B30]" />
                </div>
              ) : archiveDocuments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Keine Dokumente gefunden</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Nummer</TableHead>
                      <TableHead>Kunde</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Betrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archiveDocuments.map(doc => {
                      const badge = getDocTypeBadge(doc.document_type);
                      const isSelected = selectedArchiveDoc?.id === doc.id;
                      return (
                        <TableRow 
                          key={doc.id} 
                          className={`cursor-pointer ${isSelected ? 'bg-[#9B1B30]/10' : 'hover:bg-gray-50'}`}
                          onClick={() => setSelectedArchiveDoc(doc)}
                          data-testid={`archive-doc-${doc.id}`}
                        >
                          <TableCell>
                            <input 
                              type="radio" 
                              checked={isSelected}
                              onChange={() => setSelectedArchiveDoc(doc)}
                              className="w-4 h-4 accent-[#9B1B30]"
                            />
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
                              {badge.label}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{doc.document_number}</TableCell>
                          <TableCell>{doc.billing_name || doc.customer_number || '-'}</TableCell>
                          <TableCell>{doc.document_date}</TableCell>
                          <TableCell className="text-right">
                            {doc.gross_total?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
            
            {/* Selected Document Info */}
            {selectedArchiveDoc && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800 text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Ausgewählt: {selectedArchiveDoc.document_number} - {selectedArchiveDoc.billing_name}
                </p>
              </div>
            )}
            
            {/* Customer Selection */}
            <div className="relative">
              <Label className="font-barlow font-bold text-sm">Kunde auswählen *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Name oder Kundennummer eingeben..."
                  value={archiveCustomerSearch}
                  onChange={(e) => {
                    setArchiveCustomerSearch(e.target.value);
                    setShowArchiveCustomerDropdown(true);
                    if (!e.target.value) setArchiveCustomer('');
                  }}
                  onFocus={() => setShowArchiveCustomerDropdown(true)}
                  className="pl-9"
                  data-testid="archive-customer-search"
                />
              </div>
              
              {/* Customer Dropdown */}
              {showArchiveCustomerDropdown && archiveCustomerSearch && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredArchiveCustomers.length > 0 ? (
                    filteredArchiveCustomers.slice(0, 8).map((customer) => (
                      <div
                        key={customer.id}
                        className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-0"
                        onClick={() => handleSelectArchiveCustomer(customer)}
                      >
                        <div className="font-medium text-sm">
                          {customer.first_name} {customer.last_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          <span className="text-[#9B1B30] font-medium">{customer.customer_number}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-500 text-sm">Kein Kunde gefunden</div>
                  )}
                </div>
              )}
              
              {/* Selected Customer Display */}
              {archiveCustomer && !showArchiveCustomerDropdown && (
                <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between">
                  <span className="text-green-800 text-sm font-medium">
                    ✓ {archiveCustomerSearch}
                  </span>
                  <button 
                    type="button"
                    className="text-green-600 hover:text-green-800 text-sm"
                    onClick={() => {
                      setArchiveCustomer('');
                      setArchiveCustomerSearch('');
                    }}
                  >
                    Ändern
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleAttachDocument}
              disabled={!selectedArchiveDoc || !archiveCustomer || attaching}
              data-testid="attach-archive-submit"
            >
              <Link2 className="w-4 h-4 mr-2" />
              {attaching ? 'Wird angehängt...' : 'An Kunde anhängen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
